import type { ZudokuConfig } from "zudoku";
import "./styles.css";

const config: ZudokuConfig = {
  site: {
    title: "SeedLink Docs",
    showPoweredBy: false,
    logo: {
      src: { light: "/logo-light.svg", dark: "/logo-dark.svg" },
      alt: "SeedLink",
      width: "28px",
      href: "https://seedlink.dev",
    },
  },
  metadata: {
    title: "SeedLink Docs",
    description:
      "Unified API for cannabis seed-to-sale compliance — Metrc, BioTrack, and every system in between.",
    favicon: "/favicon.svg",
  },
  navigation: [
    {
      type: "link",
      to: "/api",
      label: "API Reference",
    },
    {
      type: "category",
      label: "User Guide",
      // Three pages, in reading order: Overview (what SeedLink is, who
      // it's for), Implementation (the actual how-to-build steps), Use
      // Cases (workflows built on those steps). Deliberately not more
      // than that -- see git history for what used to live here and
      // why it got cut.
      items: [
        { type: "doc", file: "overview", path: "/overview", label: "Overview" },
        { type: "doc", file: "implementation", path: "/implementation", label: "Implementation" },
        {
          type: "category",
          label: "Use Cases",
          collapsed: true,
          items: [
            "/onboard-via-connect",
            "/handle-reauth",
            "/create-package-from-harvest",
            "/correct-package-data",
            "/create-and-send-a-transfer",
            "/monitor-transfers",
            "/report-a-sale",
            "/submit-a-lab-sample",
            "/flag-failed-lab-results",
            "/trace-chain-of-custody",
            "/sync-inventory",
            "/backfill-historical-data",
            "/display-live-inventory",
            "/monitor-plant-counts",
            "/multi-facility-reporting",
          ],
        },
      ],
    },
  ],
  redirects: [{ from: "/", to: "/overview" }],
  // `zudoku build` always resolves NODE_ENV to "production" (Vite's own
  // build-mode default, regardless of the shell env when you run it) --
  // so this branches on dev server vs. any built artifact, not on
  // local-machine vs. deployed. Every build (including `pnpm preview`
  // run locally) fetches the live URL, so it can't drift from the real
  // API -- and that only succeeds in a browser from an origin api.
  // seedlink.dev's CORS allowlist includes. docs.seedlink.dev is
  // allowlisted; a bare `pnpm preview` on localhost is not, so expect a
  // CORS console error there -- it's a local-preview artifact, not a
  // real bug (verify with `curl -H "Origin: https://docs.seedlink.dev"`
  // instead). Only `zudoku dev` uses the bundled static file, since its
  // port isn't stable enough to whitelist (changes on every restart).
  // Regenerate apis/openapi.public.json from packages/api's
  // openapi:export:public script when the spec changes.
  apis: [
    process.env.NODE_ENV === "production"
      ? {
          type: "url",
          input: "https://api.seedlink.dev/openapi.public.json",
          path: "/api",
          options: { expandAllTags: false, showInfoPage: false },
        }
      : {
          type: "file",
          input: "./apis/openapi.public.json",
          path: "/api",
          options: { expandAllTags: false, showInfoPage: false },
        },
  ],
  // Pagefind: static search index built at `zudoku build` time, no
  // external service (Algolia/Inkeep both need a paid or hosted account).
  search: {
    type: "pagefind",
  },
  theme: {
    light: {
      background: "#fafafa",
      foreground: "#171717",
      card: "#ffffff",
      cardForeground: "#171717",
      primary: "#2d5240",
      primaryForeground: "#ffffff",
      secondary: "#f0f0f0",
      secondaryForeground: "#171717",
      muted: "#f0f0f0",
      mutedForeground: "#6b6b6b",
      accent: "#f0f0f0",
      accentForeground: "#171717",
      border: "#e2e2e2",
      input: "#e2e2e2",
      ring: "#2d5240",
      radius: "0.375rem",
    },
    dark: {
      background: "#121212",
      foreground: "#e8e8e8",
      card: "#181818",
      cardForeground: "#e8e8e8",
      primary: "#5c9b7f",
      primaryForeground: "#0a0a0a",
      secondary: "#1f1f1f",
      secondaryForeground: "#e8e8e8",
      muted: "#1f1f1f",
      mutedForeground: "#9b9b9b",
      accent: "#1f1f1f",
      accentForeground: "#e8e8e8",
      border: "#2a2a2a",
      input: "#2a2a2a",
      ring: "#5c9b7f",
      radius: "0.375rem",
    },
  },
};

export default config;
